import { useState } from "react";
import { accountService } from "@miplatform/api";
import { CONTACT_TYPES, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { buildSearchQuery, createUUID } from "@miplatform/utils";

let GET_CONTACTS_UUID = createUUID();
let GET_CONTACT_CATEGORIES_UUID = createUUID();

export const useContactsSearchViewModel = ({
  accountIds,
  referenceIds,
  onRefresh,
  selectedContacts,
  setSelectedContacts,
}) => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const [contacts, setContacts] = useState([]);
  const [activeCategories, setActiveCategories] = useState([]);

  const getContacts = (searchQuery = "") => {
    stateManager.abort(GET_CONTACTS_UUID);
    GET_CONTACTS_UUID = createUUID();

    return stateManager.run(async (signal) => {
      const filters = [];

      if (accountIds) {
        filters.push({
          or: [{ accountId: { in: accountIds } }, { accountId: null }],
        });
      }

      // Add server-side filtering only when the user entered a search term.
      const searchTerms = searchQuery.trim().split(/\s+/).filter(Boolean);
      searchTerms.forEach((searchTerm) => {
        filters.push(
          buildSearchQuery(searchTerm, [
            "firstName",
            "lastName",
            "accountName",
            "phone",
            "email",
          ])
        );
      });

      const response = await accountService.contacts.getAll({
        sort: "firstName asc",
        signal,
        ...(filters.length > 0 && {
          filters: filters.length === 1 ? filters[0] : { and: filters },
        }),
      });

      if (response.status === HTTP_STATUSES.OK) {
        const selectedIds = new Set(
          selectedContacts.map((contact) => contact.id)
        );

        setContacts(
          response.data.filter((contact) => !selectedIds.has(contact.id))
        );

        const freshContactsById = new Map(
          response.data.map((contact) => [contact.id, contact])
        );

        setSelectedContacts((previousSelectedContacts) =>
          previousSelectedContacts.map((selectedContact) => {
            const updatedContact = freshContactsById.get(selectedContact.id);

            return updatedContact
              ? {
                  ...updatedContact,
                  dependentType: selectedContact.dependentType,
                  label: selectedContact.label,
                }
              : selectedContact;
          })
        );
      }
    }, GET_CONTACTS_UUID);
  };

  const getContactCategoriesOptions = () => {
    stateManager.abort(GET_CONTACT_CATEGORIES_UUID);
    GET_CONTACT_CATEGORIES_UUID = createUUID();

    return stateManager.run(async (signal) => {
      const response = await accountService.contactCategories.getByType({
        type: CONTACT_TYPES.HOMES,
        signal,
      });
      if (response.status === HTTP_STATUSES.OK) {
        const activeCategories = response.data.filter((e) => e.isActive);

        setActiveCategories(activeCategories);
      }
    }, GET_CONTACT_CATEGORIES_UUID);
  };

  const addContactRelationship = async (contact) =>
    stateManager.run(async () => {
      const response = await accountService.contacts.createContactRelationship({
        id: contact.id,
        data: {
          accountId: contact.accountId,
          label: contact.dependentType,
          isPrimary: contact.isPrimary,
          RelatedReferenceId: referenceIds[CONTACT_TYPES.HOMES],
        },
        toastMessageConfig: {
          success: {
            text: ` Contact relationship created successfully.`,
          },
          failure: {
            text: `Failed to create contact relationship.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        onRefresh?.(contact);
        return true;
      }
      return false;
    });

  return {
    isLoading,
    contacts,
    activeCategories,
    getContacts,
    getContactCategoriesOptions,
    addContactRelationship,
  };
};
